<script setup>
// Editor da janela SEMANAL de liberação do hold (modo "aguardar resolver").
// Controlado: recebe a config (ou null=default) e emite a config completa dos 7
// dias. Espelha Campaigns::ReleaseWindow (backend). Reusado no wizard e no builder.
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import { buildDefaultReleaseWindow } from './releaseWindow';

const props = defineProps({
  modelValue: {
    type: Array,
    default: null,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();

// Exibição segunda→domingo; day_of_week 0=domingo … 6=sábado.
const DISPLAY_DAYS = [
  { dow: 1, key: 'MON' },
  { dow: 2, key: 'TUE' },
  { dow: 3, key: 'WED' },
  { dow: 4, key: 'THU' },
  { dow: 5, key: 'FRI' },
  { dow: 6, key: 'SAT' },
  { dow: 0, key: 'SUN' },
];

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, hour) => ({
  value: hour,
  label: `${String(hour).padStart(2, '0')}:00`,
}));

const currentByDay = computed(() => {
  const source =
    Array.isArray(props.modelValue) && props.modelValue.length
      ? props.modelValue
      : buildDefaultReleaseWindow();
  return new Map(source.map(day => [Number(day.day_of_week), day]));
});

const rowFor = dow => {
  const day = currentByDay.value.get(dow);
  return {
    open: day !== undefined && !day.closed_all_day,
    openHour: Number(day?.open_hour ?? 8),
    closeHour: Number(day?.close_hour ?? 18),
    // O editor mexe só na hora, mas carrega os minutos de config externa
    // (API/import) em vez de zerá-los.
    openMinutes: Number(day?.open_minutes ?? 0),
    closeMinutes: Number(day?.close_minutes ?? 0),
  };
};

// Reescreve os 7 dias a partir de uma mudança num dia (emite a config completa).
const emitDay = (dow, patch) => {
  const next = DISPLAY_DAYS.map(({ dow: day }) => {
    const row = day === dow ? { ...rowFor(day), ...patch } : rowFor(day);
    return {
      day_of_week: day,
      open_hour: row.open ? row.openHour : 0,
      open_minutes: row.open ? row.openMinutes : 0,
      close_hour: row.open ? row.closeHour : 0,
      close_minutes: row.open ? row.closeMinutes : 0,
      closed_all_day: !row.open,
    };
  }).sort((a, b) => a.day_of_week - b.day_of_week);
  emit('update:modelValue', next);
};

const key = suffix => `CAMPAIGN.WHATSAPP_OFFICIAL.OPEN_CONVERSATION.${suffix}`;
</script>

<template>
  <div class="flex flex-col gap-1.5">
    <div
      v-for="day in DISPLAY_DAYS"
      :key="day.dow"
      class="flex items-center gap-2"
    >
      <Switch
        :model-value="rowFor(day.dow).open"
        :disabled="disabled"
        @update:model-value="open => emitDay(day.dow, { open })"
      />
      <span class="w-8 shrink-0 text-sm font-medium text-n-slate-11">
        {{ t(key(`WEEKDAYS.${day.key}`)) }}
      </span>
      <template v-if="rowFor(day.dow).open">
        <ComboBox
          class="w-[4.5rem]"
          :model-value="rowFor(day.dow).openHour"
          :options="HOUR_OPTIONS"
          :disabled="disabled"
          @update:model-value="
            hour => emitDay(day.dow, { openHour: Number(hour) })
          "
        />
        <span class="i-lucide-minus size-3 shrink-0 text-n-slate-8" />
        <ComboBox
          class="w-[4.5rem]"
          :model-value="rowFor(day.dow).closeHour"
          :options="HOUR_OPTIONS"
          :disabled="disabled"
          @update:model-value="
            hour => emitDay(day.dow, { closeHour: Number(hour) })
          "
        />
      </template>
      <span v-else class="text-sm text-n-slate-10">
        {{ t(key('CLOSED')) }}
      </span>
    </div>
  </div>
</template>
